/**
 * Bloqueo de navegación para el router que NO es data router (no hay `useBlocker`).
 *
 * Una pantalla registra un bloqueador mientras tiene algo que perder (p. ej. un editor con
 * cambios sin guardar). `TransitionBrowserRouter` lo consulta antes de cada push/replace y
 * cuando el navegador ya hizo un atrás/adelante (popstate). Sin bloqueadores registrados todo
 * pasa directo y sincrónico: el router se comporta igual que siempre.
 *
 * Sin imports a propósito: lo prueba `scripts/test-catalog-editors.mjs` con un history falso.
 */

/**
 * Devuelve `null` si esta navegación no le importa (pasa sin preguntar) o una promesa que
 * resuelve `true` para seguir y `false` para quedarse.
 */
export type NavigationBlocker = (nextPathname: string) => Promise<boolean> | null;

const blockers = new Set<NavigationBlocker>();
let allowNext = false;
let allowNextTimer: ReturnType<typeof setTimeout> | undefined;

/** Registra un bloqueador; devuelve la función que lo saca. */
export function registerNavigationBlocker(blocker: NavigationBlocker): () => void {
  blockers.add(blocker);
  return () => {
    blockers.delete(blocker);
  };
}

/**
 * La próxima navegación pasa sin preguntar. Para después de guardar con éxito: el editor
 * todavía se ve «sucio» en ese render, pero ya no hay nada que perder. Si esa navegación no
 * llega enseguida, el permiso vence solo y no regala la siguiente.
 */
export function allowNextNavigation(): void {
  allowNext = true;
  clearTimeout(allowNextTimer);
  allowNextTimer = setTimeout(() => {
    allowNext = false;
  }, 1000);
}

/** El primer bloqueador al que le importa esta navegación pregunta; los demás no. */
export function checkNavigation(nextPathname: string): Promise<boolean> | null {
  if (allowNext) {
    allowNext = false;
    clearTimeout(allowNextTimer);
    return null;
  }
  for (const blocker of blockers) {
    const answer = blocker(nextPathname);
    if (answer) return answer;
  }
  return null;
}

type To = string | { pathname?: string };

interface HistoryLike {
  readonly location: { pathname: string };
  push(to: To, state?: unknown): void;
  replace(to: To, state?: unknown): void;
  go(delta: number): void;
}

const pathnameOf = (to: To, current: string): string => {
  if (typeof to !== 'string') return to.pathname ?? current;
  if (to.startsWith('?') || to.startsWith('#') || to === '') return current;
  try {
    return new URL(to, 'http://x' + current).pathname;
  } catch {
    return to;
  }
};

/**
 * Envuelve push/replace del history para que pasen por los bloqueadores, y da `filterPop`
 * para el listener: con un atrás/adelante el navegador YA cambió la URL, así que se la vuelve
 * a poner donde estaba (`go(-delta)`) y, si confirman, se repite el salto.
 */
export function createBlockingNavigation(history: HistoryLike) {
  let asking = false;
  // Pops que generamos nosotros: el que restaura la URL se ignora, el que repite el salto pasa.
  let ignoreNextPop = false;
  let allowNextPop = false;

  const guard = (to: To, go: () => void) => {
    if (asking) return;
    const answer = checkNavigation(pathnameOf(to, history.location.pathname));
    if (!answer) return go();
    asking = true;
    answer.then(
      (proceed) => {
        asking = false;
        if (proceed) go();
      },
      () => {
        asking = false;
      }
    );
  };

  return {
    push: (to: To, state?: unknown) => guard(to, () => history.push(to, state)),
    replace: (to: To, state?: unknown) => guard(to, () => history.replace(to, state)),
    /**
     * Para un update POP del listener: `true` si el router lo tiene que aplicar, `false` si
     * se frenó (la URL ya se está restaurando y, si confirman, se vuelve a navegar).
     */
    filterPop(update: { location: { pathname: string }; delta: number | null }): boolean {
      if (ignoreNextPop) {
        ignoreNextPop = false;
        return false;
      }
      if (allowNextPop) {
        allowNextPop = false;
        return true;
      }
      // Sin índice (entrada ajena a esta history) no hay a dónde volver: pasa.
      const { delta } = update;
      if (delta == null || delta === 0) return true;
      if (asking) {
        ignoreNextPop = true;
        history.go(-delta);
        return false;
      }
      const answer = checkNavigation(update.location.pathname);
      if (!answer) return true;
      asking = true;
      ignoreNextPop = true;
      history.go(-delta);
      answer.then(
        (proceed) => {
          asking = false;
          if (!proceed) return;
          allowNextPop = true;
          history.go(delta);
        },
        () => {
          asking = false;
        }
      );
      return false;
    }
  };
}
